<template>
  <ConfigPage>
    <ConfigItem>
      <v-text-field
        v-model="search"
        @change="loadItems()"
        solo
        :label="__('pesquisar')"
        single-line
        prepend-inner-icon="mdi-magnify"
        hide-details
        class="mb-2"
        clearable
      >
        <template #append-outer>
          <v-btn @click="loadItems()" icon>
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
      </v-text-field>
      <v-toolbar v-if="selected.length" flat>
        <v-toolbar-title>
          {{ selected.length }} {{ __("itens carregados") }}
          <v-btn small icon @click="selected = []">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <ScopeProvider scope="configs.products.edit">
          <v-btn @click="handleClickDelete()" icon :title="__('excluir')">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-btn @click="handleClickMerge()" icon :title="__('mesclar')">
            <v-icon>mdi-set-merge</v-icon>
          </v-btn>
          <v-btn
            @click="handleChangeVisibilityBulk()"
            icon
            :title="__('mudar visibilidade')"
          >
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </ScopeProvider>
      </v-toolbar>
      <v-toolbar v-else flat>
        <v-toolbar-title v-text="__('tipos de equipamento', 'settings')" />
        <v-spacer></v-spacer>

        <ScopeProvider scope="configs.products.edit">
          <v-btn @click="handleClickAdd()" color="primary">
            {{ __("registrar") }}
          </v-btn>
        </ScopeProvider>
      </v-toolbar>
      <v-tabs v-model="tabVisible">
        <v-tab> {{ __("Ativos") }} </v-tab>
        <v-tab> {{ __("Inativos") }} </v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-data-table
        v-if="options"
        color="primary"
        v-model="selected"
        show-select
        :items="items"
        :headers="headers"
        :server-items-length="totalItems"
        :options.sync="options"
        must-sort
      >
        <template #item.name="{ item }">
          <ScopeProvider scope="configs.products.edit">
            <a @click.prevent="handleUpdateItem(item)">
              {{ item.name }}
            </a>

            <template #not>
              {{ item.name }}
            </template>
          </ScopeProvider>
        </template>

        <template #item.product_line="{ item }">
          {{ item.product_line ? item.product_line.name : "-" }}
        </template>

        <template #item.products_count="{ item }">
          {{ item.products_count }}
          <v-btn @click="handleClickSeeProducts(item)" icon class="ml-1">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </template>

        <template #item.visible="{ item }">
          <v-switch
            @change="handleChangeVisibility(item)"
            :input-value="item.visible"
            inset
          ></v-switch>
        </template>

        <template #item.can_search_ean="{ item }">
          <v-switch
            :input-value="item.can_search_ean"
            inset
            disabled
          ></v-switch>
        </template>
      </v-data-table>
    </ConfigItem>

    <ConfirmationDialog ref="confirmDialog" />
    <EquipamentTypesFormDialog ref="formDialog" @submitted="loadItems()" />
    <MergeEquipamentTypesDialog
      ref="mergeDialog"
      @merged="loadItemsAndUnselectAll()"
    />
    <ProductsDrawer ref="productsDrawer" />
  </ConfigPage>
</template>

<script>
import ConfigPage from "@/layouts/configs/ConfigPage";
import ConfigItem from "@/layouts/configs/ConfigItem";
import i18n from "@/mixins/i18n";
import data_table from "@/tools/data_table";
import {
  index,
  destroy,
  makeVisible,
  makeInvisible,
} from "@/services/product_types";
import ToastsMixin from "@/mixins/ToastsMixin.vue";
import ConfirmationDialog from "@/components/tools/ConfirmationDialog.vue";
import MergeEquipamentTypesDialog from "@/components/equipamentTypes/MergeEquipamentTypesDialog.vue";
import EquipamentTypesFormDialog from "@/components/equipamentTypes/EquipamentTypesFormDialog.vue";
import ProductsDrawer from "@/components/equipamentTypes/ProductsDrawer.vue";
import ScopeProvider from "@/components/ScopeProvider";

export default {
  mixins: [i18n, ToastsMixin],
  components: {
    ConfigPage,
    ConfigItem,
    ConfirmationDialog,
    EquipamentTypesFormDialog,
    MergeEquipamentTypesDialog,
    ProductsDrawer,
    ScopeProvider,
  },

  data() {
    return {
      i18n_prefix: "settings",
      items: [],
      selected: [],
      options: null,
      totalItems: 0,
      search: "",
      headers: [
        {
          text: this.__("Nome", "settings"),
          value: "name",
        },
        {
          text: this.__("Linha", "settings"),
          value: "product_line",
          sortable: true,
        },
        {
          text: this.__("equipamentos", "settings"),
          value: "products_count",
          sortable: false,
        },
        {
          text: this.__("Auto EAN", "settings"),
          value: "can_search_ean",
          sortable: true,
        },
        {
          text: this.__("Visibilidade", "settings"),
          value: "visible",
          sortable: false,
        },
      ],
      loading: false,
      tabVisible: 0,
    };
  },

  methods: {
    handleClickSeeProducts(type) {
      this.$refs.productsDrawer.open(type);
    },

    handleClickAdd() {
      this.$refs.formDialog.open();
    },

    handleUpdateItem(item) {
      this.$refs.formDialog.openForUpdate(item);
    },

    handleClickMerge() {
      this.$refs.mergeDialog.open(this.selected);
    },

    loadItemsAndUnselectAll() {
      this.selected = [];
      this.loadItems();
    },

    async loadItems(options = null) {
      if (!options) {
        options = this.options;
      }

      this.loading = true;
      this.items = [];
      try {
        const response = await index({
          ...data_table.getParams(options, this.search),
          visible: this.visible,
        });

        this.items = response.data;
        this.totalItems = response.meta.total;
      } catch (error) {
        this.toastError(
          this.__(
            "não foi possível carregar tipos de equipamentos, tente novamente mais tarde",
            "settings"
          )
        );
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async handleChangeVisibility(item) {
      const payload = {
        ids: [item.id],
      };

      if (item.visible) {
        await makeInvisible(payload);
      } else {
        await makeVisible(payload);
      }
      this.loadItems();
    },

    async handleChangeVisibilityBulk() {
      if (this.selected.length < 1) {
        return;
      }

      const ids = this.selected.map((item) => item.id);

      if (this.visible) {
        await makeInvisible({
          ids,
        });
      } else {
        await makeVisible({ ids });
      }

      this.selected = [];
      this.loadItems();
    },

    handleClickDelete() {
      this.$refs.confirmDialog.confirm(
        this.__("excluir os itens carregados?", "settings"),
        async ({ close, startLoading, stopLoading }) => {
          startLoading();
          try {
            await this.destroySelected();
            close();
            this.toast(this.__("itens removidos", "settings"));
            this.selected = [];
            this.loadItems();
          } catch (error) {
            if (error.response?.status === 400) {
              this.toastError(error.response.data.message);
              return;
            }
            this.toastError("Não foi possível excluir as categorias");
          } finally {
            stopLoading();
          }
        },
        null,
        {
          color: "red",
          confirmText: this.__("excluir", "settings"),
          description: `
    <ul>
      <li>${this.__("apaga todas as categorias selecionadas", "settings")}</li>
      <li>${this.__("não é possível reverter", "settings")}</li>
      <li>${this.__("interrompe caso haja algum equipamento", "settings")}</li>
    </ul>
  `,
        }
      );
    },

    destroySelected() {
      return destroy({
        ids: this.selected.map((item) => item.id),
      });
    },

    async handleClickMakeInvisibleBulk() {},
  },

  watch: {
    options(value) {
      this.loadItems(value);
    },
    visible() {
      this.loadItems();
    },
  },

  computed: {
    selectedText() {
      return this.selected.map((item) => item.name).join(", ");
    },
    visible() {
      return this.tabVisible === 0 ? true : false;
    },
  },

  created() {
    this.options = data_table.getOptions();
  },
};
</script>
